/**
 * Room × rate plan combination helpers.
 *
 * Every combo carries the id of the property whose rooms/rate plans it was
 * built from. Rate plan codes are identical across properties (HTRET, HTRETB,
 * …) so a selection built from the wrong property looks correct in the UI —
 * only the ids differ. The tag lets the payload mappers drop combos that do
 * not belong to the property they are about to be sent under, instead of
 * silently creating a promo against another property's rateplan ids.
 *
 * The tag must come from the property the rooms/rate plans were *fetched* for,
 * never from the currently selected property: the two disagreeing is exactly
 * the bug this guards against.
 */

/** Property ids arrive as both numbers and strings — compare as strings. */
export const normalizePropertyKey = (propertyId) =>
  propertyId == null ? null : String(propertyId);

/**
 * Build every room × rate plan combination for one property.
 * @param {Array} roomsData rooms belonging to `propertyId`
 * @param {Array} ratePlansData rate plans belonging to `propertyId`
 * @param {string|number|null} propertyId owner of `roomsData`/`ratePlansData`
 */
export function buildAllRoomRateplanCombos(
  roomsData = [],
  ratePlansData = [],
  propertyId = null
) {
  const owner = normalizePropertyKey(propertyId);
  const combos = [];
  (roomsData || []).forEach((room) => {
    (ratePlansData || []).forEach((rp) => {
      if (room?.code && rp?.code) {
        combos.push({
          mRight: room.code,
          mLeft: rp.code,
          rateplanId: rp.id,
          roomtypeId: room.id,
          propertyId: owner,
        });
      }
    });
  });
  return combos;
}

/** Stamp combos that were built elsewhere with their owning property. */
export function tagCombosWithProperty(combos = [], propertyId = null) {
  const owner = normalizePropertyKey(propertyId);
  return (combos || []).map((combo) => ({ ...combo, propertyId: owner }));
}

/**
 * Owner of a combo. An untagged combo is not ownerless — it was built from
 * whatever rooms/rate plans were loaded at the time, so callers that know
 * which property that was pass it as `implicitOwnerId` and the combo is
 * attributed to it. Only when the owner is genuinely unknowable (no tag, no
 * implicit owner) does this return null.
 */
export function resolveComboOwner(combo, implicitOwnerId = null) {
  const tagged = normalizePropertyKey(combo?.propertyId);
  return tagged === null ? normalizePropertyKey(implicitOwnerId) : tagged;
}

/**
 * Combos with an unknowable owner are kept: they predate provenance tracking
 * (saved drafts, flows that cannot know the owner) and dropping them would
 * silently empty a valid selection. Only an explicit mismatch is rejected.
 *
 * Pass `implicitOwnerId` wherever the untagged case *is* knowable — without it
 * an untagged combo matches every property, which is what let one property's
 * rateplan ids be submitted under another's.
 */
export function belongsToProperty(combo, propertyId, implicitOwnerId = null) {
  const owner = resolveComboOwner(combo, implicitOwnerId);
  if (owner === null) return true;
  const target = normalizePropertyKey(propertyId);
  return target === null || owner === target;
}

export function filterCombosForProperty(
  combos = [],
  propertyId,
  implicitOwnerId = null
) {
  return (combos || []).filter((combo) =>
    belongsToProperty(combo, propertyId, implicitOwnerId)
  );
}

/**
 * Drop the provenance tag before sending combos to the legacy promo API,
 * which only knows the four original fields.
 */
export function stripComboPropertyIds(combos = []) {
  return (combos || []).map(({ propertyId, ...rest }) => rest);
}
